/**
 * InservicePdfBlock — the optional fill-and-sign PDF (post-test / handout) attached to an in-service.
 */
import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Loader2, PenTool, Trash2, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export async function uploadInservicePdf(file) {
  const form = new FormData();
  form.append("file", file);
  const res = await axios.post(`${API}/settings/orientation-kit/upload-document`, form, { headers: { "Content-Type": "multipart/form-data" } });
  return {
    pdf_storage_path: res.data.storage_path,
    pdf_content_type: res.data.content_type,
    pdf_filename: res.data.original_filename || file.name,
    pdf_fillable_form: res.data.fillable_form || null,
  };
}

export default function InservicePdfBlock({ item, idx, readonly, onChange, onEditFields }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const fieldCount = item.pdf_fillable_form?.fields?.length || 0;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const patch = await uploadInservicePdf(file);
      onChange(patch);
      toast.success(patch.pdf_fillable_form ? `PDF attached — ${patch.pdf_fillable_form.fields.length} fields detected` : "PDF attached — use Edit fields to add fill-in boxes");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (!item.pdf_storage_path) {
    if (readonly) return null;
    return (
      <div>
        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-1 text-xs" data-testid={`inservice-attach-pdf-${idx}`}>
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} Attach PDF to fill & sign (post-test, handout…)
        </Button>
        <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={upload} />
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-purple-200 bg-purple-50/50 px-3 py-2" data-testid={`inservice-pdf-${idx}`}>
      <FileText className="w-4 h-4 text-purple-600" />
      <span className="text-sm text-slate-800 truncate max-w-[220px]" title={item.pdf_filename}>{item.pdf_filename || "PDF"}</span>
      <Badge variant="outline" className={`text-[10px] ${fieldCount ? "bg-purple-100 text-purple-700 border-purple-200" : "bg-slate-100 text-slate-600"}`} data-testid={`inservice-pdf-fields-${idx}`}>
        {fieldCount ? `${fieldCount} fields${item.pdf_fillable_form?.auto_detected ? " · auto-detected" : ""}` : "Sign only — no fields yet"}
      </Badge>
      <span className="text-[11px] text-slate-500">Completed copy is filed to the office automatically</span>
      {!readonly && (
        <div className="ml-auto flex gap-1">
          <Button variant="ghost" size="sm" onClick={onEditFields} className="gap-1 text-xs text-purple-700" data-testid={`inservice-edit-fields-${idx}`}>
            <PenTool className="w-3.5 h-3.5" /> {fieldCount ? "Edit fields" : "Make fillable"}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-1 text-xs" data-testid={`inservice-replace-pdf-${idx}`}>
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Replace
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onChange({ pdf_storage_path: null, pdf_content_type: null, pdf_filename: null, pdf_fillable_form: null })} className="text-rose-600" data-testid={`inservice-remove-pdf-${idx}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
          <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={upload} />
        </div>
      )}
    </div>
  );
}
